import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import mapboxgl from 'mapbox-gl';
import Papa, { ParseResult } from 'papaparse';
import 'mapbox-gl/dist/mapbox-gl.css';
import { debounce } from 'lodash';

import { FlightData, AirplaneState, AnimationState, FlightPath, DisplayOptions } from '../types';
import { MapProvider } from '../MapContext';
import Airplane from './Airplane';
import AnimationControls from './AnimationControls';
import Header, { MapTheme } from './Header';
import Sidebar, { getTrackColor } from './Sidebar';
import LoadingScreen from './LoadingScreen';
import FollowModeHUD from './FollowModeHUD';

interface TimestampMapping {
  [key: string]: number;
}

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

if (!MAPBOX_TOKEN) {
  throw new Error('REACT_APP_MAPBOX_ACCESS_TOKEN environment variable is not set');
}

mapboxgl.accessToken = MAPBOX_TOKEN;

const getAltitudeColor = (altitude: number): string => {
  if (altitude < 5000) return '#3CAEA3';
  if (altitude < 15000) return '#F6D55C';
  if (altitude < 25000) return '#ED553B';
  return '#9370DB';
};

const MapComponent: React.FC = () => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const [mapInstance, setMapInstance] = useState<mapboxgl.Map | null>(null);

  const [flightData, setFlightData] = useState<FlightData[]>([]);
  const [airplanes, setAirplanes] = useState<AirplaneState[]>([]);
  const [filteredAirplanes, setFilteredAirplanes] = useState<AirplaneState[]>([]);
  const [timestampMap, setTimestampMap] = useState<TimestampMapping>({});
  const [uniquePlaneIds, setUniquePlaneIds] = useState<string[]>([]);
  const [selectedPlaneIds, setSelectedPlaneIds] = useState<string[]>([]);
  const [currentTimeframeInfo] = useState<{
    start: number;
    end: number;
    planeId: string;
  } | null>(null);
  const [animation, setAnimation] = useState<AnimationState>({
    isPlaying: false,
    currentTimestamp: 1,
    maxTimestamp: 1,
    speed: 1,
  });

  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);

  const animationRef = useRef<number | null>(null);
  const lastGeoJsonRef = useRef<GeoJSON.FeatureCollection | null>(null);

  const [flightPaths, setFlightPaths] = useState<FlightPath[]>([]);
  const [displayOptions, setDisplayOptions] = useState<DisplayOptions>({
    showFlightPaths: true,
    showFullPaths: false,
    pathColorMode: 'single',
    pathOpacity: 0.9,
    pathWidth: 3
  });

  const [keepInView, setKeepInView] = useState(false);
  const [trackedPlaneIds, setTrackedPlaneIds] = useState<string[]>([]);
  const [followPlaneId, setFollowPlaneId] = useState<string | null>(null);
  const [, setSupermanMode] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [mapTheme, setMapTheme] = useState<MapTheme>('dark');
  const [mapBrightness, setMapBrightness] = useState(120);

  // Superman easter egg — type "superman" to activate, "planes" to deactivate
  useEffect(() => {
    let buffer = '';
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key.length !== 1) return; // ignore shift, ctrl, etc.
      buffer += e.key.toLowerCase();
      if (buffer.length > 20) buffer = buffer.slice(-20);

      let action: 'superman' | 'planes' | null = null;
      if (buffer.endsWith('superman')) action = 'superman';
      else if (buffer.endsWith('planes')) action = 'planes';

      if (!action) return;
      buffer = '';

      const map = mapRef.current;
      if (!map) return;

      if (action === 'superman') {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          if (map.hasImage('superman-icon')) map.removeImage('superman-icon');
          map.addImage('superman-icon', img);
          map.setLayoutProperty('aircraft-circles', 'icon-image', 'superman-icon');
          map.setLayoutProperty('aircraft-circles', 'icon-size', [
            'interpolate', ['linear'], ['zoom'],
            2, 0.08, 6, 0.12, 10, 0.2, 14, 0.35
          ]);
          map.setPaintProperty('aircraft-circles', 'icon-opacity', ['coalesce', ['to-number', ['get', 'opacity'], 1], 1]);
        };
        img.src = '/superman.png';
        setSupermanMode(true);
      } else {
        map.setLayoutProperty('aircraft-circles', 'icon-image', 'airplane-icon');
        map.setLayoutProperty('aircraft-circles', 'icon-size', [
          'interpolate', ['linear'], ['zoom'],
          2, 0.3, 6, 0.45, 10, 0.65, 14, 0.9
        ]);
        map.setPaintProperty('aircraft-circles', 'icon-color', [
          'case',
          ['==', ['get', 'altitude'], 'ground'], '#3CAEA3',
          ['<', ['to-number', ['get', 'altitude'], 0], 5000], '#3CAEA3',
          ['<', ['to-number', ['get', 'altitude'], 0], 15000], '#F6D55C',
          ['<', ['to-number', ['get', 'altitude'], 0], 25000], '#ED553B',
          '#9370DB'
        ]);
        map.setPaintProperty('aircraft-circles', 'icon-opacity', ['coalesce', ['to-number', ['get', 'opacity'], 0.95], 0.95]);
        setSupermanMode(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Initialize the map
  useEffect(() => {
    if (!mapContainer.current || mapRef.current) return;

    try {
      const map = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/dark-v11',
        center: [-98, 39],
        zoom: 4,
        maxZoom: 16,
        minZoom: 2,
        renderWorldCopies: false,
        preserveDrawingBuffer: false,
        attributionControl: false,
        antialias: true
      });

      mapRef.current = map;

      map.addControl(
        new mapboxgl.NavigationControl({
          showCompass: true,
          showZoom: true,
          visualizePitch: false
        }),
        'top-right'
      );

      map.once('idle', () => {
        // Add GeoJSON source for all aircraft (GPU-rendered)
        map.addSource('aircraft', {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: [] }
        });

        // Create airplane icon from SVG for GPU rendering
        const svgStr = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24"><path fill="#ffffff" d="M12,2L4.5,20.29L5.21,21L12,18L18.79,21L19.5,20.29L12,2Z"/></svg>`;
        const img = new Image(32, 32);
        img.onload = () => {
          if (!map.hasImage('airplane-icon')) {
            map.addImage('airplane-icon', img, { sdf: true });
          }
          // Mark map as ready only after icon is loaded
          const canvas = map.getCanvas();
          if (canvas) canvas.style.filter = 'brightness(1.2)';
          setMapLoaded(true);
          setMapInstance(map);
        };
        img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgStr);

        // Aircraft symbol layer — rotated airplane icons, colored by altitude
        map.addLayer({
          id: 'aircraft-circles',
          type: 'symbol',
          source: 'aircraft',
          layout: {
            'icon-image': 'airplane-icon',
            'icon-size': [
              'interpolate', ['linear'], ['zoom'],
              2, 0.3,
              6, 0.45,
              10, 0.65,
              14, 0.9
            ],
            'icon-rotate': ['get', 'rotation'],
            'icon-rotation-alignment': 'map',
            'icon-allow-overlap': true,
            'icon-ignore-placement': true,
            'text-field': ['step', ['zoom'], '', 7, ['get', 'icao_id']],
            'text-size': 10,
            'text-offset': [0, 1.4],
            'text-anchor': 'top',
            'text-allow-overlap': false,
            'text-optional': true,
          },
          paint: {
            'icon-color': [
              'case',
              ['==', ['get', 'altitude'], 'ground'], '#3CAEA3',
              ['<', ['to-number', ['get', 'altitude'], 0], 5000], '#3CAEA3',
              ['<', ['to-number', ['get', 'altitude'], 0], 15000], '#F6D55C',
              ['<', ['to-number', ['get', 'altitude'], 0], 25000], '#ED553B',
              '#9370DB'
            ],
            'icon-opacity': ['coalesce', ['to-number', ['get', 'opacity'], 0.95], 0.95],
            'text-color': '#ffffff',
            'text-halo-color': 'rgba(0,0,0,0.7)',
            'text-halo-width': 1,
          }
        });

        // Click — track and follow plane (HUD replaces popup)
        map.on('click', 'aircraft-circles', (e) => {
          if (!e.features || e.features.length === 0) return;
          const props = e.features[0].properties;
          const icaoId = props?.icao_id || 'Unknown';

          setTrackedPlaneIds([icaoId]);
          setDisplayOptions(prev => ({ ...prev, showFlightPaths: true, pathColorMode: 'altitude' }));
          setFollowPlaneId(icaoId);
        });

        // Hover tooltip
        const hoverPopup = new mapboxgl.Popup({
          closeButton: false,
          closeOnClick: false,
          className: 'aircraft-hover-popup',
          maxWidth: '180px',
          offset: 14,
        });

        map.on('mousemove', 'aircraft-circles', (e) => {
          map.getCanvas().style.cursor = 'pointer';
          if (!e.features || e.features.length === 0) return;
          const props = e.features[0].properties;
          const coords = (e.features[0].geometry as any).coordinates.slice();
          const alt = props?.altitude === 'ground' ? 'Ground' : `${Number(props?.altitude || 0).toLocaleString()} ft`;

          hoverPopup
            .setLngLat(coords)
            .setHTML(`<strong>${props?.icao_id}</strong> · ${alt}`)
            .addTo(map);
        });

        map.on('mouseleave', 'aircraft-circles', () => {
          map.getCanvas().style.cursor = '';
          hoverPopup.remove();
        });

        // mapLoaded and mapInstance are set inside img.onload above
      });

      map.on('error', (e) => {
        console.error('Map error:', e);
        setMapError('Map error occurred');
      });
    } catch (err) {
      console.error('Map initialization error:', err);
      setMapError('Failed to initialize map');
    }

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        setMapInstance(null);
      }
    };
  }, []);

  // Theme switching — swap Mapbox style and re-add layers after style loads
  const handleThemeChange = useCallback((theme: MapTheme) => {
    setMapTheme(theme);
    const map = mapRef.current;
    if (!map) return;

    const style = theme === 'light'
      ? 'mapbox://styles/mapbox/light-v11'
      : 'mapbox://styles/mapbox/dark-v11';

    // Update CSS theme class on the root
    document.documentElement.setAttribute('data-theme', theme);

    map.setStyle(style);

    // After style loads, re-add the aircraft source and layer
    map.once('style.load', () => {
      if (!map.getSource('aircraft')) {
        map.addSource('aircraft', {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: [] }
        });
      }

      // Re-create airplane icon
      const svgStr = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24"><path fill="#ffffff" d="M12,2L4.5,20.29L5.21,21L12,18L18.79,21L19.5,20.29L12,2Z"/></svg>`;
      const img = new Image(32, 32);
      img.onload = () => {
        if (!map.hasImage('airplane-icon')) {
          map.addImage('airplane-icon', img, { sdf: true });
        }

        if (!map.getLayer('aircraft-circles')) {
          map.addLayer({
            id: 'aircraft-circles',
            type: 'symbol',
            source: 'aircraft',
            layout: {
              'icon-image': 'airplane-icon',
              'icon-size': ['interpolate', ['linear'], ['zoom'], 2, 0.3, 6, 0.45, 10, 0.65, 14, 0.9],
              'icon-rotate': ['get', 'rotation'],
              'icon-rotation-alignment': 'map',
              'icon-allow-overlap': true,
              'icon-ignore-placement': true,
              'text-field': ['step', ['zoom'], '', 7, ['get', 'icao_id']],
              'text-size': 10,
              'text-offset': [0, 1.4],
              'text-anchor': 'top',
              'text-allow-overlap': false,
              'text-optional': true,
            },
            paint: {
              'icon-color': [
                'case',
                ['==', ['get', 'altitude'], 'ground'], '#3CAEA3',
                ['<', ['to-number', ['get', 'altitude'], 0], 5000], '#3CAEA3',
                ['<', ['to-number', ['get', 'altitude'], 0], 15000], '#F6D55C',
                ['<', ['to-number', ['get', 'altitude'], 0], 25000], '#ED553B',
                '#9370DB'
              ],
              'icon-opacity': ['coalesce', ['to-number', ['get', 'opacity'], 0.95], 0.95],
              'text-color': theme === 'light' ? '#333333' : '#ffffff',
              'text-halo-color': theme === 'light' ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.7)',
              'text-halo-width': 1,
            }
          });
        }
      };
      img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgStr);
    });
  }, []);

  // Brightness — apply CSS filter to the map canvas
  const handleBrightnessChange = useCallback((value: number) => {
    setMapBrightness(value);
    const canvas = mapRef.current?.getCanvas();
    if (canvas) {
      canvas.style.filter = `brightness(${value / 100})`;
    }
  }, []);

  // Filter airplanes — only used for individually tracked planes (DOM markers)
  // The GPU layer shows all planes regardless
  useEffect(() => {
    if (airplanes.length === 0) return;

    // Only create DOM markers for specifically filtered (tracked) planes
    // When selectedPlaneIds equals all planes, don't create DOM markers - GPU handles it
    if (selectedPlaneIds.length === 0 || selectedPlaneIds.length === uniquePlaneIds.length) {
      setFilteredAirplanes([]);
    } else {
      const filtered = airplanes.filter(airplane =>
        selectedPlaneIds.includes(String(airplane.airplaneId))
      );
      setFilteredAirplanes(filtered);
    }
  }, [airplanes, selectedPlaneIds, uniquePlaneIds.length]);

  // Load flight data from CSV
  useEffect(() => {
    const loadData = async () => {
      try {
        const response = await fetch('/plane_locations_by_minute_dash.csv');
        if (!response.ok) {
          throw new Error(`Failed to load flight data: ${response.status}`);
        }
        const csvText = await response.text();

        Papa.parse<FlightData>(csvText, {
          header: true,
          dynamicTyping: true,
          complete: (results: ParseResult<FlightData>) => {
            const parsedData = results.data.filter(d =>
              d.icao_id !== undefined &&
              d.datetime !== undefined &&
              d.latitude !== undefined &&
              d.longitude !== undefined
            );

            // Collect all unique timestamps and sort chronologically
            const tsSet = new Set<string>();
            parsedData.forEach(d => { if (d.datetime) tsSet.add(d.datetime); });
            const sortedTimestamps = Array.from(tsSet).sort();

            const uniqueTimestamps: TimestampMapping = {};
            sortedTimestamps.forEach((ts, i) => {
              uniqueTimestamps[ts] = i + 1;
            });

            const planeIdSet = new Set(parsedData.map(d => String(d.icao_id)));
            const planeIds = Array.from(planeIdSet);
            setUniquePlaneIds(planeIds);
            setSelectedPlaneIds(planeIds);

            setFlightData(parsedData);
            setTimestampMap(uniqueTimestamps);

            const maxTimestamp = Object.keys(uniqueTimestamps).length;
            setAnimation(prev => ({
              ...prev,
              maxTimestamp,
              currentTimestamp: 1,
            }));

          },
          error: (error: Error) => {
            console.error('Error parsing CSV:', error);
            setMapError('Failed to parse flight data');
          }
        });
      } catch (error) {
        console.error('Error loading flight data:', error);
        setMapError('Failed to load flight data');
      }
    };

    loadData();
  }, []);

  // Interpolated animation — smooth movement between data points
  const animStateRef = useRef({ currentTimestamp: 1, maxTimestamp: 1, speed: 1, interp: 0 });

  // Keep ref in sync with state (for non-playing updates like scrubbing)
  useEffect(() => {
    animStateRef.current.currentTimestamp = animation.currentTimestamp;
    animStateRef.current.maxTimestamp = animation.maxTimestamp;
    animStateRef.current.speed = animation.speed;
  }, [animation.currentTimestamp, animation.maxTimestamp, animation.speed]);



  // Pre-build per-plane timelines for linear interpolation across data gaps
  const planeTimelines = useMemo(() => {
    const timelines = new Map<string, { t: number; lng: number; lat: number; rotation: number; altitude: number | string; speed: number }[]>();

    for (const flight of flightData) {
      const id = String(flight.icao_id);
      if (!timelines.has(id)) timelines.set(id, []);

      const lng = typeof flight.longitude === 'number' ? flight.longitude : parseFloat(String(flight.longitude));
      const lat = typeof flight.latitude === 'number' ? flight.latitude : parseFloat(String(flight.latitude));

      timelines.get(id)!.push({
        t: timestampMap[flight.datetime],
        lng,
        lat,
        rotation: flight.direction,
        altitude: flight.altitude,
        speed: typeof flight.speed === 'number' ? flight.speed : parseFloat(String(flight.speed)) || 0,
      });
    }

    timelines.forEach(points => {
      points.sort((a, b) => a.t - b.t);
    });

    return timelines;
  }, [flightData, timestampMap]);

  // Interpolated position update for a given timestamp (used when paused/scrubbing)
  const updateInterpolatedPositions = useCallback((timestampIndex: number, timelines?: typeof planeTimelines) => {
    const tl = timelines || planeTimelines;
    if (tl.size === 0) return;
    const effectiveTime = timestampIndex;
    const result: AirplaneState[] = [];

    tl.forEach((points, planeId) => {
      if (effectiveTime < points[0].t || effectiveTime > points[points.length - 1].t) return;

      let lo = 0, hi = points.length - 1;
      while (lo < hi - 1) {
        const mid = (lo + hi) >> 1;
        if (points[mid].t <= effectiveTime) lo = mid;
        else hi = mid;
      }

      const prev = points[lo];
      const next = points[hi];
      let lng: number, lat: number, rotation: number, altitude: number | string;

      if (prev.t === next.t || lo === hi) {
        lng = prev.lng; lat = prev.lat; rotation = prev.rotation; altitude = prev.altitude;
      } else {
        const frac = (effectiveTime - prev.t) / (next.t - prev.t);
        lng = prev.lng + (next.lng - prev.lng) * frac;
        lat = prev.lat + (next.lat - prev.lat) * frac;
        altitude = prev.altitude;
        let rotDiff = next.rotation - prev.rotation;
        if (rotDiff > 180) rotDiff -= 360;
        if (rotDiff < -180) rotDiff += 360;
        rotation = prev.rotation + rotDiff * frac;
      }

      result.push({
        airplaneId: planeId,
        position: [lng, lat] as [number, number],
        rotation, altitude,
        speed: prev.speed,
        timestamp: timestampIndex,
      });
    });

    setAirplanes(result);

    const geojson: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features: result.map(a => ({
        type: 'Feature' as const,
        geometry: { type: 'Point' as const, coordinates: [a.position[0], a.position[1]] },
        properties: {
          icao_id: String(a.airplaneId),
          altitude: String(a.altitude),
          rotation: a.rotation,
          opacity: 0.95,
        }
      }))
    };
    lastGeoJsonRef.current = geojson;

    if (mapRef.current?.getSource('aircraft')) {
      (mapRef.current.getSource('aircraft') as mapboxgl.GeoJSONSource).setData(geojson);
    }
  }, [planeTimelines]);

  // Render loop — always runs when data is loaded. Advances time only when playing.
  const isPlayingRef = useRef(animation.isPlaying);
  useEffect(() => { isPlayingRef.current = animation.isPlaying; }, [animation.isPlaying]);

  useEffect(() => {
    if (planeTimelines.size === 0 || !mapLoaded) return;

    const state = animStateRef.current;
    let lastTime = 0;
    let lastStateUpdate = 0;

    const frameLoop = () => {
      const now = performance.now();

      if (lastTime === 0) {
        lastTime = now;
        animationRef.current = requestAnimationFrame(frameLoop);
        return;
      }

      const delta = Math.min(now - lastTime, 50);
      lastTime = now;

      // Only advance time when playing
      if (isPlayingRef.current) {
        state.interp += delta * (state.speed / 1000);

        while (state.interp >= 1) {
          state.interp -= 1;
          state.currentTimestamp = state.currentTimestamp >= state.maxTimestamp ? 1 : state.currentTimestamp + 1;

          setAnimation(prev => ({
            ...prev,
            currentTimestamp: state.currentTimestamp,
          }));
        }
      }

      const effectiveTime = state.currentTimestamp + state.interp;

      const interpolated: (AirplaneState & { opacity?: number })[] = [];

      planeTimelines.forEach((points, planeId) => {
        if (effectiveTime < points[0].t || effectiveTime > points[points.length - 1].t) return;

        let lo = 0, hi = points.length - 1;
        while (lo < hi - 1) {
          const mid = (lo + hi) >> 1;
          if (points[mid].t <= effectiveTime) lo = mid;
          else hi = mid;
        }

        const prev = points[lo];
        const next = points[hi];

        let lng: number, lat: number, rotation: number, altitude: number | string;

        if (prev.t === next.t || lo === hi) {
          lng = prev.lng;
          lat = prev.lat;
          rotation = prev.rotation;
          altitude = prev.altitude;
        } else {
          const frac = (effectiveTime - prev.t) / (next.t - prev.t);
          lng = prev.lng + (next.lng - prev.lng) * frac;
          lat = prev.lat + (next.lat - prev.lat) * frac;
          altitude = prev.altitude;

          let rotDiff = next.rotation - prev.rotation;
          if (rotDiff > 180) rotDiff -= 360;
          if (rotDiff < -180) rotDiff += 360;
          rotation = prev.rotation + rotDiff * frac;
        }

        interpolated.push({
          airplaneId: planeId,
          position: [lng, lat] as [number, number],
          rotation,
          altitude,
          speed: prev.speed,
          timestamp: state.currentTimestamp,
        });
      });

      if (now - lastStateUpdate > 100) {
        lastStateUpdate = now;
        setAirplanes(interpolated);
      }

      if (mapRef.current?.getSource('aircraft')) {
        const geojson: GeoJSON.FeatureCollection = {
          type: 'FeatureCollection',
          features: interpolated.filter(a => (a.opacity ?? 1) > 0.01).map(a => ({
            type: 'Feature' as const,
            geometry: { type: 'Point' as const, coordinates: [a.position[0], a.position[1]] },
            properties: {
              icao_id: String(a.airplaneId),
              altitude: String(a.altitude),
              rotation: a.rotation,
              opacity: a.opacity ?? 0.95
            }
          }))
        };
        lastGeoJsonRef.current = geojson;
        (mapRef.current.getSource('aircraft') as mapboxgl.GeoJSONSource).setData(geojson);
      }

      animationRef.current = requestAnimationFrame(frameLoop);
    };

    animationRef.current = requestAnimationFrame(frameLoop);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
    };
  }, [planeTimelines, mapLoaded]);

  // Control handlers
  const handlePlay = () => {
    setAnimation(prev => ({ ...prev, isPlaying: true }));
  };

  const handlePause = () => {
    setAnimation(prev => ({ ...prev, isPlaying: false }));
  };

  const handleReset = () => {
    setAnimation(prev => ({
      ...prev,
      currentTimestamp: 1,
      isPlaying: false,
    }));
    updateInterpolatedPositions(1);
    setTrackedPlaneIds([]);
    setFollowPlaneId(null);
  };

  const handleSpeedChange = (speed: number) => {
    setAnimation(prev => ({ ...prev, speed }));
  };

  const handleTimelineChange = (timestamp: number) => {
    setAnimation(prev => ({
      ...prev,
      currentTimestamp: timestamp,
      isPlaying: false,
    }));
    updateInterpolatedPositions(timestamp);
  };

  const handleStepForward = useCallback(() => {
    setAnimation(prev => {
      const next = Math.min(prev.currentTimestamp + 1, prev.maxTimestamp);
      updateInterpolatedPositions(next);
      return { ...prev, currentTimestamp: next, isPlaying: false };
    });
  }, [updateInterpolatedPositions]);

  const handleStepBackward = useCallback(() => {
    setAnimation(prev => {
      const next = Math.max(prev.currentTimestamp - 1, 1);
      updateInterpolatedPositions(next);
      return { ...prev, currentTimestamp: next, isPlaying: false };
    });
  }, [updateInterpolatedPositions]);

  // Compute time range string
  const getTimeRange = (): string => {
    const timestamps = Object.keys(timestampMap);
    if (timestamps.length < 2) return '';
    const fmt = (ts: string) => {
      try {
        const d = new Date(ts);
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) +
          ' ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
      } catch { return ts; }
    };
    return `${fmt(timestamps[0])} → ${fmt(timestamps[timestamps.length - 1])}`;
  };

  // Memoize displayed airplanes
  const displayedAirplanes = useMemo(() => filteredAirplanes, [filteredAirplanes]);

  // Update viewState when the map moves (debounced)
  useEffect(() => {
    if (!mapRef.current) return;

    const handleMapMove = debounce(() => {
      // View state updates handled internally by mapbox
    }, 100);

    mapRef.current.on('move', handleMapMove);

    return () => {
      if (mapRef.current) {
        mapRef.current.off('move', handleMapMove);
      }
    };
  }, []);

  // Get current timestamp string
  const getCurrentTimestampString = (): string => {
    const timestamps = Object.keys(timestampMap);
    if (timestamps.length === 0 || animation.currentTimestamp <= 0 ||
        animation.currentTimestamp > timestamps.length) {
      return "No data";
    }

    const rawTimestamp = timestamps[animation.currentTimestamp - 1];

    try {
      const date = new Date(rawTimestamp);

      const formattedDate = date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });

      const formattedTime = date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });

      return `${formattedDate} - ${formattedTime}`;
    } catch (error) {
      return rawTimestamp;
    }
  };

  // Center view — on tracked planes if any, otherwise all visible planes
  const handleCenterView = useCallback(() => {
    if (!mapRef.current || airplanes.length === 0) return;

    try {
      // Use tracked planes if any, otherwise all visible
      const planesToCenter = trackedPlaneIds.length > 0
        ? airplanes.filter(a => trackedPlaneIds.includes(String(a.airplaneId)))
        : airplanes;

      if (planesToCenter.length === 0) return;

      const coordinates = planesToCenter.map(plane => plane.position);

      const bounds = coordinates.reduce((bounds, coord) => {
        return bounds.extend([coord[0], coord[1]]);
      }, new mapboxgl.LngLatBounds(coordinates[0], coordinates[0]));

      mapRef.current.fitBounds(bounds, {
        padding: { top: 100, bottom: 120, left: 100, right: 100 },
        maxZoom: 10,
        duration: 1500
      });
    } catch (error) {
      console.error('Error centering view:', error);
    }
  }, [airplanes, trackedPlaneIds]);

  // Flight paths only render for explicitly tracked planes
  const filteredFlightPaths = useMemo(() => {
    if (!displayOptions.showFlightPaths || trackedPlaneIds.length === 0) return [];

    return flightPaths.filter(path =>
      trackedPlaneIds.includes(path.id)
    );
  }, [flightPaths, trackedPlaneIds, displayOptions.showFlightPaths]);

  // Update flight path layers on the map
  const updateFlightPathLayers = useCallback(() => {
    if (!mapRef.current) return;

    try {
      // Hide paths not in filtered list
      const layers = mapRef.current.getStyle().layers || [];
      const pathLayers = layers.filter(layer => layer.id.startsWith('flight-path-'));

      pathLayers.forEach(layer => {
        const planeId = layer.id.replace('flight-path-', '').split('-segment-')[0];
        const isVisible = filteredFlightPaths.some(path => path.id === planeId);

        if (!isVisible || !displayOptions.showFlightPaths) {
          mapRef.current!.setLayoutProperty(layer.id, 'visibility', 'none');
        }
      });

      if (!displayOptions.showFlightPaths) return;

      // Process each flight path
      filteredFlightPaths.forEach((path) => {
        const baseLayerId = `flight-path-${path.id}`;
        const baseSourceId = `flight-path-source-${path.id}`;

        let visibleCoordinates: [number, number][] = [];
        let visibleAltitudes: number[] = [];

        if (displayOptions.showFullPaths) {
          visibleCoordinates = path.coordinates;
          visibleAltitudes = path.altitudes;
        } else if (animation.currentTimestamp) {
          const currentTime = animation.currentTimestamp;
          let endIndex = -1;

          if (path.timestampIndices && path.timestampIndices.has(currentTime)) {
            endIndex = path.timestampIndices.get(currentTime)!;
          } else {
            for (let i = path.timestamps.length - 1; i >= 0; i--) {
              if (path.timestamps[i] <= currentTime) {
                endIndex = i;
                break;
              }
            }
          }

          visibleCoordinates = endIndex >= 0 ? path.coordinates.slice(0, endIndex + 1) : [];
          visibleAltitudes = endIndex >= 0 ? path.altitudes.slice(0, endIndex + 1) : [];
        }

        if (visibleCoordinates.length < 2) {
          if (mapRef.current) {
            const pathSegmentLayers = mapRef.current.getStyle().layers.filter(
              layer => layer.id.startsWith(`${baseLayerId}-segment-`)
            );
            pathSegmentLayers.forEach(layer => {
              mapRef.current?.setLayoutProperty(layer.id, 'visibility', 'none');
            });
          }
          return;
        }

        if (displayOptions.pathColorMode === 'altitude') {
          // Remove single path layer if exists
          if (mapRef.current?.getLayer(baseLayerId)) {
            mapRef.current.removeLayer(baseLayerId);
          }
          if (mapRef.current?.getSource(baseSourceId)) {
            mapRef.current.removeSource(baseSourceId);
          }

          // Remove ALL old segment layers first (coordinates change when toggling full/progressive)
          if (mapRef.current) {
            const oldSegments = mapRef.current.getStyle().layers.filter(
              layer => layer.id.startsWith(`${baseLayerId}-segment-`)
            );
            oldSegments.forEach(layer => {
              try {
                mapRef.current?.removeLayer(layer.id);
                const segSrc = layer.id.replace('flight-path-', 'flight-path-source-');
                if (mapRef.current?.getSource(segSrc)) mapRef.current.removeSource(segSrc);
              } catch (e) { /* ignore */ }
            });
          }

          const getAltitudeRange = (alt: number): number => {
            if (alt < 5000) return 0;
            if (alt < 15000) return 1;
            if (alt < 25000) return 2;
            return 3;
          };

          let currentAltitudeRange = -1;
          let segmentStart = 0;

          for (let i = 0; i < visibleCoordinates.length; i++) {
            const altitudeRange = getAltitudeRange(visibleAltitudes[i]);

            if (altitudeRange !== currentAltitudeRange || i === visibleCoordinates.length - 1) {
              if (i > segmentStart) {
                const segmentCoords = visibleCoordinates.slice(segmentStart, i + 1);
                const segmentId = `${baseLayerId}-segment-${segmentStart}-${i}`;
                const segmentSourceId = `${baseSourceId}-segment-${segmentStart}-${i}`;
                const segmentColor = getAltitudeColor(visibleAltitudes[segmentStart]);

                const lineData: GeoJSON.Feature<GeoJSON.LineString> = {
                  type: 'Feature',
                  properties: { altitude: visibleAltitudes[segmentStart] },
                  geometry: { type: 'LineString', coordinates: segmentCoords }
                };

                if (mapRef.current?.getSource(segmentSourceId)) {
                  const source = mapRef.current.getSource(segmentSourceId) as mapboxgl.GeoJSONSource;
                  source.setData(lineData);

                  if (mapRef.current.getLayer(segmentId)) {
                    mapRef.current.setLayoutProperty(segmentId, 'visibility', 'visible');
                    mapRef.current.setPaintProperty(segmentId, 'line-color', segmentColor);
                    mapRef.current.setPaintProperty(segmentId, 'line-width', displayOptions.pathWidth);
                    mapRef.current.setPaintProperty(segmentId, 'line-opacity', displayOptions.pathOpacity);
                  }
                } else if (mapRef.current) {
                  try {
                    mapRef.current.addSource(segmentSourceId, { type: 'geojson', data: lineData });
                    mapRef.current.addLayer({
                      id: segmentId,
                      type: 'line',
                      source: segmentSourceId,
                      layout: { 'line-join': 'round', 'line-cap': 'round', 'visibility': 'visible' },
                      paint: {
                        'line-color': segmentColor,
                        'line-width': displayOptions.pathWidth,
                        'line-opacity': displayOptions.pathOpacity,
                        'line-offset': 0,
                        'line-dasharray': [2, 1]
                      }
                    });
                  } catch (e) {
                    console.error(`Error creating segment ${segmentId}:`, e);
                  }
                }
              }

              segmentStart = i;
              currentAltitudeRange = altitudeRange;
            }
          }
        } else {
          // Single color mode - remove segment layers
          if (mapRef.current) {
            const pathSegmentLayers = mapRef.current.getStyle().layers.filter(
              layer => layer.id.startsWith(`${baseLayerId}-segment-`)
            );

            pathSegmentLayers.forEach(layer => {
              try {
                if (mapRef.current?.getLayer(layer.id)) {
                  mapRef.current.removeLayer(layer.id);
                }
                const segSourceId = layer.id.replace('flight-path-', 'flight-path-source-');
                if (mapRef.current?.getSource(segSourceId)) {
                  mapRef.current.removeSource(segSourceId);
                }
              } catch (e) {
                console.error(`Error removing segment layer ${layer.id}:`, e);
              }
            });
          }

          const lineData: GeoJSON.Feature<GeoJSON.LineString> = {
            type: 'Feature',
            properties: {},
            geometry: { type: 'LineString', coordinates: visibleCoordinates }
          };

          // Use distinct color per tracked plane
          const trackIndex = trackedPlaneIds.indexOf(path.id);
          const lineColor = trackIndex >= 0 ? getTrackColor(trackIndex) : '#F6D55C';

          try {
            if (mapRef.current?.getSource(baseSourceId)) {
              const source = mapRef.current.getSource(baseSourceId) as mapboxgl.GeoJSONSource;
              source.setData(lineData);

              if (mapRef.current.getLayer(baseLayerId)) {
                mapRef.current.setLayoutProperty(baseLayerId, 'visibility', 'visible');
                mapRef.current.setPaintProperty(baseLayerId, 'line-color', lineColor);
                mapRef.current.setPaintProperty(baseLayerId, 'line-width', displayOptions.pathWidth);
                mapRef.current.setPaintProperty(baseLayerId, 'line-opacity', displayOptions.pathOpacity);
                mapRef.current.setPaintProperty(baseLayerId, 'line-offset', 0);
                mapRef.current.setPaintProperty(
                  baseLayerId,
                  'line-dasharray',
                  displayOptions.pathColorMode === 'single' ? [1, 0] : [2, 1]
                );
              }
            } else if (mapRef.current) {
              mapRef.current.addSource(baseSourceId, { type: 'geojson', data: lineData });
              mapRef.current.addLayer({
                id: baseLayerId,
                type: 'line',
                source: baseSourceId,
                layout: { 'line-join': 'round', 'line-cap': 'round', 'visibility': 'visible' },
                paint: {
                  'line-color': lineColor,
                  'line-width': displayOptions.pathWidth,
                  'line-opacity': displayOptions.pathOpacity,
                  'line-offset': 0,
                  'line-dasharray': displayOptions.pathColorMode === 'single' ? [1, 0] : [2, 1]
                }
              }, 'aircraft-circles');
            }
          } catch (e) {
            console.error(`Error with flight path layer ${baseLayerId}:`, e);
          }
        }
      });
    } catch (error) {
      console.error('Error updating flight path layers:', error);
    }
  }, [filteredFlightPaths, displayOptions, animation.currentTimestamp, trackedPlaneIds]);

  // Update flight paths when relevant state changes
  useEffect(() => {
    if (mapLoaded && mapRef.current) {
      updateFlightPathLayers();
    }
  }, [mapLoaded, updateFlightPathLayers, filteredFlightPaths, displayOptions.showFlightPaths, displayOptions.showFullPaths, animation.currentTimestamp]);

  const handleDisplayOptionsChange = useCallback((newOptions: DisplayOptions) => {
    setDisplayOptions(newOptions);
  }, []);

  // Generate flight paths from flight data
  const generateFlightPaths = useCallback(() => {
    if (flightData.length === 0 || Object.keys(timestampMap).length === 0) return;

    const planeGroups: Record<string, FlightData[]> = {};

    flightData.forEach(flight => {
      const id = String(flight.icao_id);
      if (!planeGroups[id]) {
        planeGroups[id] = [];
      }
      planeGroups[id].push(flight);
    });

    const paths: FlightPath[] = Object.keys(planeGroups).map(planeId => {
      const flights = planeGroups[planeId].sort((a, b) => {
        return timestampMap[a.datetime] - timestampMap[b.datetime];
      });

      const timestampIndices = new Map<number, number>();
      const coordinates: [number, number][] = [];
      const timestamps: number[] = [];
      const altitudes: number[] = [];

      let minLng = Infinity;
      let maxLng = -Infinity;
      let minLat = Infinity;
      let maxLat = -Infinity;

      flights.forEach((flight) => {
        const lng = typeof flight.longitude === 'number' ? flight.longitude : parseFloat(String(flight.longitude));
        const lat = typeof flight.latitude === 'number' ? flight.latitude : parseFloat(String(flight.latitude));

        const altitude = typeof flight.altitude === 'number'
          ? flight.altitude
          : flight.altitude === 'ground'
            ? 0
            : parseFloat(String(flight.altitude)) || 0;

        if (isNaN(lng) || isNaN(lat) || (lng === 0 && lat === 0)) return;

        minLng = Math.min(minLng, lng);
        maxLng = Math.max(maxLng, lng);
        minLat = Math.min(minLat, lat);
        maxLat = Math.max(maxLat, lat);

        coordinates.push([lng, lat]);
        altitudes.push(altitude);

        const timestamp = timestampMap[flight.datetime];
        timestamps.push(timestamp);
        timestampIndices.set(timestamp, coordinates.length - 1);
      });

      if (coordinates.length < 2) return null;

      return {
        id: planeId,
        coordinates,
        timestamps,
        altitudes,
        boundingBox: { minLng, maxLng, minLat, maxLat },
        timestampIndices
      };
    }).filter(Boolean) as FlightPath[];

    setFlightPaths(paths);
  }, [flightData, timestampMap]);

  // Generate flight paths when data is loaded
  useEffect(() => {
    if (flightData.length > 0 && Object.keys(timestampMap).length > 0) {
      generateFlightPaths();
    }
  }, [flightData, timestampMap, generateFlightPaths]);

  // Follow aircraft with smooth interpolated camera
  const prevFollowRef = useRef<string | null>(null);
  const followTargetRef = useRef<[number, number] | null>(null);
  const followBearingRef = useRef<number>(0);
  const followCurrentRef = useRef<[number, number] | null>(null);
  const followAnimRef = useRef<number | null>(null);
  const followDelayRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const lerpAngle = (a: number, b: number, t: number): number => {
    let delta = ((b - a + 540) % 360) - 180;
    return a + delta * t;
  };

  // Smooth camera loop — interpolates position and bearing
  useEffect(() => {
    if (!followPlaneId || !mapRef.current) {
      if (followAnimRef.current) {
        cancelAnimationFrame(followAnimRef.current);
        followAnimRef.current = null;
      }
      if (followDelayRef.current) {
        clearTimeout(followDelayRef.current);
        followDelayRef.current = null;
      }
      followTargetRef.current = null;
      followCurrentRef.current = null;
      // Reset camera on exit
      if (mapRef.current && prevFollowRef.current) {
        mapRef.current.easeTo({ pitch: 0, bearing: 0, duration: 1000 });
      }
      prevFollowRef.current = null;
      return;
    }

    const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
    const SMOOTHING = 0.04;

    const tick = () => {
      if (!mapRef.current || !followTargetRef.current) {
        followAnimRef.current = requestAnimationFrame(tick);
        return;
      }

      if (!followCurrentRef.current) {
        followCurrentRef.current = [...followTargetRef.current];
      }

      const [curLng, curLat] = followCurrentRef.current;
      const [tgtLng, tgtLat] = followTargetRef.current;

      const newLng = lerp(curLng, tgtLng, SMOOTHING);
      const newLat = lerp(curLat, tgtLat, SMOOTHING);

      followCurrentRef.current = [newLng, newLat];

      // Skip center/bearing updates while map is easing (e.g. zoom button animation)
      if (!mapRef.current.isEasing()) {
        mapRef.current.setCenter([newLng, newLat]);

        // Smoothly rotate bearing to match plane heading
        const currentBearing = mapRef.current.getBearing();
        const newBearing = lerpAngle(currentBearing, followBearingRef.current, 0.02);
        mapRef.current.setBearing(newBearing);
      }

      followAnimRef.current = requestAnimationFrame(tick);
    };

    // Wait for the flyTo animation to finish before starting smooth tracking
    followDelayRef.current = setTimeout(() => {
      followAnimRef.current = requestAnimationFrame(tick);
    }, 1600);

    return () => {
      if (followAnimRef.current) {
        cancelAnimationFrame(followAnimRef.current);
      }
      if (followDelayRef.current) {
        clearTimeout(followDelayRef.current);
      }
    };
  }, [followPlaneId]);

  // Update follow target when airplane data changes
  useEffect(() => {
    if (!followPlaneId || !mapRef.current) return;

    const plane = airplanes.find(a => String(a.airplaneId) === followPlaneId);
    if (!plane) return;

    const isNewFollow = prevFollowRef.current !== followPlaneId;
    prevFollowRef.current = followPlaneId;

    const isGrounded = plane.altitude === 'ground' || plane.altitude === 0;

    // Don't spin the map when the plane is on the ground
    if (!isGrounded) {
      followBearingRef.current = plane.rotation;
    }

    if (isNewFollow) {
      followCurrentRef.current = [plane.position[0], plane.position[1]];
      followTargetRef.current = [plane.position[0], plane.position[1]];
      mapRef.current.flyTo({
        center: [plane.position[0], plane.position[1]],
        zoom: 8,
        pitch: isGrounded ? 0 : 45,
        bearing: isGrounded ? 0 : plane.rotation,
        duration: 1500,
      });
    } else {
      followTargetRef.current = [plane.position[0], plane.position[1]];
    }
  }, [airplanes, followPlaneId]);

  // Drag map → exit follow mode (only user-initiated drags, not zoom controls)
  useEffect(() => {
    if (!followPlaneId || !mapRef.current) return;
    const onDragStart = (e: any) => {
      // Only exit follow on actual user drag (originalEvent exists), not programmatic moves
      if (e.originalEvent) setFollowPlaneId(null);
    };
    mapRef.current.on('dragstart', onDragStart);
    return () => { mapRef.current?.off('dragstart', onDragStart); };
  }, [followPlaneId]);

  // Escape key → exit follow mode
  useEffect(() => {
    if (!followPlaneId) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFollowPlaneId(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [followPlaneId]);

  // Keep tracked planes in view (skip when following a specific plane)
  useEffect(() => {
    if (!mapRef.current || !keepInView || trackedPlaneIds.length === 0 || followPlaneId) return;

    const tracked = airplanes.filter(a => trackedPlaneIds.includes(String(a.airplaneId)));
    if (tracked.length === 0) return;

    try {
      const coordinates = tracked.map(p => p.position);
      const bounds = coordinates.reduce((b, coord) => {
        return b.extend([coord[0], coord[1]]);
      }, new mapboxgl.LngLatBounds(coordinates[0], coordinates[0]));

      mapRef.current.fitBounds(bounds, {
        padding: { top: 80, bottom: 100, left: 80, right: 80 },
        maxZoom: 10,
        duration: 500
      });
    } catch (error) {
      console.error('Error keeping in view:', error);
    }
  }, [airplanes, trackedPlaneIds, keepInView, followPlaneId]);

  const isDataLoaded = flightData.length > 0 && mapLoaded;

  return (
    <MapProvider value={mapInstance}>
      <LoadingScreen isLoading={!isDataLoaded} />

      <div className="map-container" style={{ width: '100%', height: '100vh', position: 'relative' }}>
        <div ref={mapContainer} style={{ width: '100%', height: '100%' }} />
        <div className="map-vignette" />

        <Header
          theme={mapTheme}
          brightness={mapBrightness}
          onThemeChange={handleThemeChange}
          onBrightnessChange={handleBrightnessChange}
          onToggleSidebar={() => setSidebarOpen(prev => !prev)}
        />

        <Sidebar
          planeIds={uniquePlaneIds}
          displayOptions={displayOptions}
          onDisplayOptionsChange={handleDisplayOptionsChange}
          planeCount={airplanes.length}
          totalPlanes={uniquePlaneIds.length}
          onCenterView={handleCenterView}
          keepInView={keepInView}
          onKeepInViewChange={setKeepInView}
          trackedPlaneIds={trackedPlaneIds}
          onTrackedPlanesChange={setTrackedPlaneIds}
          followPlaneId={followPlaneId}
          onFollowPlane={setFollowPlaneId}
          airplanes={airplanes}
          mobileOpen={sidebarOpen}
          onMobileClose={() => setSidebarOpen(false)}
        />

        {followPlaneId && (() => {
          const plane = airplanes.find(a => String(a.airplaneId) === followPlaneId);
          return plane ? (
            <FollowModeHUD
              planeId={String(plane.airplaneId)}
              altitude={plane.altitude}
              heading={plane.rotation}
              speed={plane.speed ?? null}
              onStopFollow={() => setFollowPlaneId(null)}
            />
          ) : null;
        })()}

        <AnimationControls
          animation={animation}
          timestampString={getCurrentTimestampString()}
          timeRange={getTimeRange()}
          planeCount={airplanes.length}
          timeframeInfo={currentTimeframeInfo}
          onPlay={handlePlay}
          onPause={handlePause}
          onReset={handleReset}
          onSpeedChange={handleSpeedChange}
          onTimelineChange={handleTimelineChange}
          onStepForward={handleStepForward}
          onStepBackward={handleStepBackward}
        />

        {mapLoaded && displayedAirplanes && displayedAirplanes.map((airplane: AirplaneState) => (
          <Airplane key={airplane.airplaneId} airplane={airplane} />
        ))}

        {mapError && (
          <div className="error-overlay">{mapError}</div>
        )}
      </div>
    </MapProvider>
  );
};

export default MapComponent;
